// src/pages/AnimeIndex.jsx
import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { animeDBService } from '../../services/AnimeDB/animeDB.service.remote'
import { loadStatusMap } from '../../store/slices/watchlist.slice'
import { AnimeList } from '../../cmps/AnimeDB/AnimeList'
import { AnimeIndexHelmet } from '../../cmps/Helments/AnimeIndexHelmet'

// ברירת מחדל של פילטר
const DEFAULT_FILTER = {
  title: '',
  year: '',
  status: '',
  sort: '',
  top100: false,
  excludeMatureContent: true,
  genres: [],
  themes: [],
  demographics: [],
  limit: 30,
}

// עוזר לשליפות מערכים מתוך URL
function getArrayParam(params, key) {
  return params.getAll(key).filter(Boolean)
}

export function AnimeIndex() {
  const dispatch = useDispatch()
  const userId = useSelector(s => s.userModule.loggedInUser?._id)

  const observerRef = useRef(null)
  const loadedIdsRef = useRef(new Set())
  const loadedPagesRef = useRef(new Set())

  const [searchParams] = useSearchParams()

  // בונים את ה-baseFilter מכל הפרמטרים ב-URL
const baseFilter = useMemo(() => ({
  ...DEFAULT_FILTER,
  title: searchParams.get('title') || DEFAULT_FILTER.title,
  year: searchParams.get('year') || DEFAULT_FILTER.year,
  status: searchParams.get('status') || DEFAULT_FILTER.status,
  sort: searchParams.get('sort') || DEFAULT_FILTER.sort,
  top100: searchParams.get('top100') === 'true',
  excludeMatureContent: searchParams.get('excludeMatureContent') !== 'false',
  limit: Number(searchParams.get('limit')) || DEFAULT_FILTER.limit,
  genres: getArrayParam(searchParams, 'genres'),
  themes: getArrayParam(searchParams, 'themes'),
  demographics: getArrayParam(searchParams, 'demographics'),
}), [searchParams.toString()]) // 🔁 תלות ישירה במחרוזת ה-URL

  // React Query v5: useInfiniteQuery({ ... })
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['animes', baseFilter],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await animeDBService.query({ ...baseFilter, page: pageParam })
      return { animes: res, page: pageParam }
    },
    getNextPageParam: last => {
      return last.animes.length < baseFilter.limit
        ? undefined
        : last.page + 1
    },
    keepPreviousData: true,
  })

  const animes = data?.pages.flatMap(p => p.animes) || []
  const statusMap = useSelector(s => s.watchlist.statusMap[userId] || {})

  // טעינת סטטוסים ל־IDs חדשים
  useEffect(() => {
    if (!userId || isLoading || !animes.length) return

    data.pages.forEach((page, idx) => {
      const pageNum = idx + 1
      if (loadedPagesRef.current.has(pageNum)) return

      const newIds = page.animes
        .map(a => a.mal_id)
        .filter(id => !loadedIdsRef.current.has(id))

      if (newIds.length) {
        dispatch(loadStatusMap({
          userId,
          listType: 'watchlist',
          anizoneIds: newIds,
        }))
        newIds.forEach(id => loadedIdsRef.current.add(id))
      }
      loadedPagesRef.current.add(pageNum)
    })
  }, [dispatch, userId, isLoading, data?.pages])

  // Infinite scroll
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return

    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && fetchNextPage(),
      { threshold: 1 }
    )
    if (observerRef.current) obs.observe(observerRef.current)
    return () => {
      if (observerRef.current) obs.unobserve(observerRef.current)
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <>
      <AnimeIndexHelmet />
      <div className="container mx-auto p-4 text-white">
        <AnimeList animes={animes} statusMap={statusMap} />

        {/* נקודת עצירה לסקרול */}
        <div ref={observerRef} className="h-10" />

        {isFetchingNextPage && (
          <p className="text-center text-muted mt-4">טוען עוד אנימות...</p>
        )}
        {!isLoading && !animes.length && (
          <p className="text-center text-muted">לא נמצאו אנימות בהתאמה</p>
        )}
      </div>
    </>
  )
}
